/**
 * @file TodoGroupsBody.tsx
 * @brief The meeting To-dos tab's list, grouped (F1a, DES 31-10-2026,
 *        ADR-062): List | By category | By topic | By person | By due date |
 *        By status, a smart default, the person's pick remembered.
 *
 * @details
 * The rows themselves are the tab's own cells (`renderCell`), so ticking,
 * evidence, reassign, dismiss and Asana stay exactly as they were. This adds
 * the small view switch, collapsible headings with counts, the heading menus
 * (rename / merge a category, rename a topic) and the chip that moves one
 * to-do. A meeting nobody labelled (switch off, old meeting) shows the plain
 * list and no switch, exactly as before.
 *
 * @references features/todos/todoViews.ts, features/todos/TodoGroupingControls.tsx
 * @author DES (prepared with Claude)
 * @date 31-10-2026
 */

import { useMemo, useState, type ReactNode } from "react";
import { GridBand, GridCells } from "@/features/analytics/components/GridBlock";
import {
  CategoryHeadingMenu,
  MoveTodoMenu,
  TodoGroupHeading,
  TodoViewMenu,
  TopicHeadingMenu,
} from "@/features/todos/TodoGroupingControls";
import { useTodoCategories } from "@/features/todos/todoGroupingApi";
import {
  VIEWS_FOR,
  countedDueDay,
  groupTodos,
  localToday,
  readRememberedView,
  rememberView,
  resolveView,
  type TodoViewKey,
  type ViewFacts,
} from "@/features/todos/todoViews";
import type { MeetingTodo } from "../schemas";

export function TodoGroupsBody({
  todos,
  meetingId,
  personOf,
  canMove,
  canEditTopics,
  renderCell,
}: {
  /** Live (not dismissed) to-dos, in the meeting's order. */
  todos: MeetingTodo[];
  meetingId: string | null | undefined;
  /** The name shown for a to-do's owner (the tab's resolver). */
  personOf: (t: MeetingTodo) => string | null;
  /** May this viewer move this to-do (owner, assignee, follow-up, admin)? */
  canMove: (t: MeetingTodo) => boolean;
  /** Meeting owner / company admin: may rename a topic heading. */
  canEditTopics: boolean;
  /** One to-do's cell; `chip` = the category chip for the row's chip line. */
  renderCell: (t: MeetingTodo, i: number, count: number, chip: ReactNode) => ReactNode;
}) {
  const labelled = todos.some((t) => t.category || t.topic);
  const facts = (t: MeetingTodo): ViewFacts => ({
    category: t.category,
    categoryId: t.categoryId,
    topic: t.topic,
    person: personOf(t),
    dueDay: countedDueDay(t),
    done: Boolean(t.completed),
  });
  const [picked, setPicked] = useState<TodoViewKey | null>(() => readRememberedView("meeting"));
  const view = labelled ? resolveView("meeting", picked, todos, facts) : "list";
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const anyMovable = labelled && Boolean(meetingId) && todos.some(canMove);
  const categories = useTodoCategories(meetingId ?? null, anyMovable);
  const categoryList = categories.data?.categories ?? [];
  const canManageCategories = Boolean(categories.data?.canManage);

  const topics = useMemo(
    () => [...new Set(todos.map((t) => String(t.topic ?? "").trim()).filter(Boolean))],
    [todos],
  );
  const groups = groupTodos(todos, view, facts, localToday());

  const chipFor = (t: MeetingTodo): ReactNode =>
    labelled && meetingId && t.id && canMove(t) ? (
      <MoveTodoMenu
        meetingId={meetingId}
        todoId={t.id}
        category={t.category}
        categoryId={t.categoryId}
        topic={t.topic}
        categories={categoryList}
        topics={topics}
      />
    ) : null;

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      {labelled ? (
        <GridBand className="flex items-center gap-2 py-2.5">
          <TodoViewMenu
            value={view}
            options={VIEWS_FOR.meeting}
            onChange={(next) => {
              setPicked(next);
              rememberView("meeting", next);
            }}
          />
        </GridBand>
      ) : null}
      {view === "list" ? (
        <GridCells className="grid-cols-1">
          {todos.map((t, i) => renderCell(t, i, todos.length, chipFor(t)))}
        </GridCells>
      ) : (
        groups.map((g) => {
          const isCollapsed = collapsed.has(g.key);
          const menu =
            g.kind === "category" && g.categoryId && canManageCategories ? (
              <CategoryHeadingMenu categoryId={g.categoryId} name={g.label} categories={categoryList} />
            ) : g.kind === "topic" && g.topic && canEditTopics && meetingId ? (
              <TopicHeadingMenu meetingId={meetingId} topic={g.topic} />
            ) : null;
          return (
            <div key={g.key}>
              <GridBand className="py-2">
                <TodoGroupHeading
                  label={g.label}
                  count={g.items.length}
                  collapsed={isCollapsed}
                  onToggle={() => toggle(g.key)}
                  tone={g.tone}
                  menu={menu}
                />
              </GridBand>
              {isCollapsed ? null : (
                <GridCells className="grid-cols-1">
                  {g.items.map((t, i) => renderCell(t, i, g.items.length, chipFor(t)))}
                </GridCells>
              )}
            </div>
          );
        })
      )}
    </>
  );
}
